/**
 * Frontend mirror of customer-invoices/services/VatTreatmentService.resolveTreatment.
 * Used by the invoice form to auto-fill the vat_treatment when a customer
 * is picked. The backend remains the system of record — this helper is a
 * UX convenience so the form shows the same result the backend would
 * compute on save.
 *
 * Architectural framing:
 *
 *   - `exempt` and `out_of_scope` derive from the TENANT (vat_status). The
 *     seller's whole activity is momsfri (vårdcentral) or under threshold;
 *     not a per-customer tag.
 *   - `reverse_charge_eu` derives from customer country + VAT number.
 *   - `reverse_charge_construction` derives from customer.is_construction_business.
 *   - `reverse_charge_electronics` is invoice-level (amount-based) — not auto.
 *   - `taxable` is the default for a registered tenant invoicing a domestic
 *     customer.
 *
 * Sources:
 * - Mervärdesskattelag (2023:200) 6 kap, 10 kap, 16 kap.
 * - Skatteverket — Sälja tjänster till andra EU-länder.
 * - Skatteverket — Omvänd skattskyldighet inom byggsektorn (ML 16 kap 13 §).
 *
 * Keep this file in lockstep with
 * `accounting-backend-alt1/customer-invoices/services/VatTreatmentService.js`.
 */

export const VAT_TREATMENTS = Object.freeze([
  'taxable',
  'exempt',
  'out_of_scope',
  'reverse_charge_eu',
  'reverse_charge_construction',
  'reverse_charge_electronics'
]);

// Greece appears under both 'GR' (ISO 3166) and 'EL' (the VAT-registry
// prefix Skatteverket and the EU VIES use, e.g. EL999999999). Either input
// resolves to EU country.
const EU_MEMBER_CODES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES',
  'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV',
  'MT', 'NL', 'PL', 'PT', 'RO', 'SI', 'SK'
]);

const EU_COUNTRY_NAMES = new Set([
  'austria', 'belgium', 'bulgaria', 'cyprus', 'czech republic', 'czechia',
  'germany', 'denmark', 'estonia', 'spain', 'finland', 'france', 'greece',
  'croatia', 'hungary', 'ireland', 'italy', 'lithuania', 'luxembourg',
  'latvia', 'malta', 'netherlands', 'poland', 'portugal', 'romania',
  'slovenia', 'slovakia',
  'österrike', 'belgien', 'bulgarien', 'cypern', 'tjeckien', 'tyskland',
  'danmark', 'estland', 'spanien', 'finland', 'frankrike', 'grekland',
  'kroatien', 'ungern', 'irland', 'italien', 'litauen', 'luxemburg',
  'lettland', 'malta', 'nederländerna', 'polen', 'portugal', 'rumänien',
  'slovenien', 'slovakien'
]);

const SE_COUNTRY_NAMES = new Set(['sweden', 'sverige', 'se']);

export function isSweden(country) {
  if (!country) return true;
  return SE_COUNTRY_NAMES.has(String(country).trim().toLowerCase());
}

export function isEuCountry(country) {
  if (!country) return false;
  const trimmed = String(country).trim();
  if (trimmed.length === 2 && EU_MEMBER_CODES.has(trimmed.toUpperCase())) {
    return true;
  }
  return EU_COUNTRY_NAMES.has(trimmed.toLowerCase());
}

export function hasValidLookingVatNumber(vatNumber) {
  if (!vatNumber) return false;
  const trimmed = String(vatNumber).trim();
  if (trimmed.length < 4) return false;
  return /^[A-Z0-9 .-]+$/i.test(trimmed);
}

/**
 * Resolve the default VAT treatment for an invoice.
 *
 * @param {object} customer
 * @param {string|null} customer.country
 * @param {string|null} customer.vatnumber
 * @param {boolean|number|null} customer.is_construction_business
 * @param {object} [options]
 * @param {string} [options.vatStatus] — tenant.vat_status from Redux
 *   `selectedCompany.vatStatus`. One of 'registered', 'exempt', 'not_registered'.
 * @returns {string} one of VAT_TREATMENTS
 */
export function resolveTreatment(customer, options = {}) {
  const vatStatus = options.vatStatus;
  if (vatStatus === 'exempt' || vatStatus === 'not_registered') {
    return 'exempt';
  }

  if (!customer || typeof customer !== 'object') {
    return 'taxable';
  }

  const country = customer.country;

  if (isEuCountry(country) && hasValidLookingVatNumber(customer.vatnumber)) {
    return 'reverse_charge_eu';
  }

  if (isSweden(country) && Boolean(Number(customer.is_construction_business || 0))) {
    return 'reverse_charge_construction';
  }

  if (!isSweden(country) && !isEuCountry(country)) {
    return 'out_of_scope';
  }

  return 'taxable';
}

/**
 * Swedish labels for the VAT-treatment ENUM values, used by the override UI
 * on the invoice form. Customer form does not expose this enum directly —
 * customer-level VAT data is country + vatnumber + is_construction_business
 * only.
 */
export const VAT_TREATMENT_LABELS = Object.freeze({
  taxable: 'Skattepliktig',
  exempt: 'Undantagen (ML 10 kap)',
  out_of_scope: 'Utanför momsens tillämpningsområde',
  reverse_charge_eu: 'Omvänd skattskyldighet (EU-tjänster)',
  reverse_charge_construction: 'Omvänd skattskyldighet (bygg)',
  reverse_charge_electronics: 'Omvänd skattskyldighet (elektronik)'
});

/**
 * Country dropdown options for the customer form.
 * Values are stored as Swedish names — `resolveTreatment` already accepts
 * Swedish names alongside ISO codes and English names. Keep grouped so the
 * dropdown reads well: Sverige first (default), then EU members (the
 * auto-derivation group), then common non-EU.
 *
 * The construction-business checkbox is mutually exclusive with non-Sverige
 * country in the form UI — see the disable-on-conflict logic in
 * NewCustomerModal / ViewCustomerDetailed. ML 16 kap 13 § (omvänd
 * skattskyldighet inom byggsektorn) only applies between two SE construction
 * businesses; for a foreign customer the routing goes through ML 6 kap
 * (reverse_charge_eu) instead and the bygg-flag is meaningless.
 */
export const COUNTRY_OPTIONS = Object.freeze([
  { value: 'Sverige', label: 'Sverige', group: 'Sverige' },
  // EU member states — alphabetical Swedish.
  { value: 'Belgien', label: 'Belgien', group: 'EU' },
  { value: 'Bulgarien', label: 'Bulgarien', group: 'EU' },
  { value: 'Cypern', label: 'Cypern', group: 'EU' },
  { value: 'Danmark', label: 'Danmark', group: 'EU' },
  { value: 'Estland', label: 'Estland', group: 'EU' },
  { value: 'Finland', label: 'Finland', group: 'EU' },
  { value: 'Frankrike', label: 'Frankrike', group: 'EU' },
  { value: 'Grekland', label: 'Grekland', group: 'EU' },
  { value: 'Irland', label: 'Irland', group: 'EU' },
  { value: 'Italien', label: 'Italien', group: 'EU' },
  { value: 'Kroatien', label: 'Kroatien', group: 'EU' },
  { value: 'Lettland', label: 'Lettland', group: 'EU' },
  { value: 'Litauen', label: 'Litauen', group: 'EU' },
  { value: 'Luxemburg', label: 'Luxemburg', group: 'EU' },
  { value: 'Malta', label: 'Malta', group: 'EU' },
  { value: 'Nederländerna', label: 'Nederländerna', group: 'EU' },
  { value: 'Polen', label: 'Polen', group: 'EU' },
  { value: 'Portugal', label: 'Portugal', group: 'EU' },
  { value: 'Rumänien', label: 'Rumänien', group: 'EU' },
  { value: 'Slovakien', label: 'Slovakien', group: 'EU' },
  { value: 'Slovenien', label: 'Slovenien', group: 'EU' },
  { value: 'Spanien', label: 'Spanien', group: 'EU' },
  { value: 'Tjeckien', label: 'Tjeckien', group: 'EU' },
  { value: 'Tyskland', label: 'Tyskland', group: 'EU' },
  { value: 'Ungern', label: 'Ungern', group: 'EU' },
  { value: 'Österrike', label: 'Österrike', group: 'EU' },
  // Common non-EU. Picking any of these → out_of_scope in resolveTreatment.
  { value: 'Norge', label: 'Norge', group: 'Övriga' },
  { value: 'Schweiz', label: 'Schweiz', group: 'Övriga' },
  { value: 'Storbritannien', label: 'Storbritannien', group: 'Övriga' },
  { value: 'Island', label: 'Island', group: 'Övriga' },
  { value: 'USA', label: 'USA', group: 'Övriga' },
  { value: 'Kanada', label: 'Kanada', group: 'Övriga' },
  { value: 'Australien', label: 'Australien', group: 'Övriga' },
  { value: 'Japan', label: 'Japan', group: 'Övriga' },
  { value: 'Kina', label: 'Kina', group: 'Övriga' },
  { value: 'Indien', label: 'Indien', group: 'Övriga' }
]);
